import translations from '../i18n/translations';
import { showImperativeToast } from '../providers/UIProvider';
import { diagnosticLog } from './diagnosticLogger';
import { resolveExclusiveDeviceName, logLocalOutputShared } from './localOutputLogging';
import { resolveZoneDisplayName } from './zoneDisplayName';
import { useMaestroStore } from '../store/maestroStore';
import {
  exclusiveFallbackDeviceNoticeKey,
  shouldShowExclusiveFallbackDeviceNotice,
} from './usbExclusiveFallbackNotice';

export type ExclusiveFallbackReason = 'format' | 'device_busy';

/** Phase 2A connector error prefixes + legacy exclusive strings. */
const RECOVERABLE_EXCLUSIVE_MARKERS = [
  'exclusive_device_busy:',
  'exclusive_format_unsupported:',
  'exclusive_mode_not_allowed:',
  'format not supported in exclusive mode',
] as const;

function t(key: string, vars?: Record<string, string | number>): string {
  let lang = 'en';
  try {
    lang = localStorage.getItem('maestro_language') || 'en';
  } catch {
    /* ignore */
  }
  const catalog = translations as Record<string, Record<string, string>>;
  let str = catalog[lang]?.[key] || catalog.en?.[key] || key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      str = str.split(`{${k}}`).join(String(v));
    }
  }
  return str;
}

/** True when exclusive open failed but shared-mode playback is expected to work. */
export function isRecoverableExclusiveFailure(errMsg: string): boolean {
  const lower = String(errMsg || '').toLowerCase();
  return RECOVERABLE_EXCLUSIVE_MARKERS.some((marker) => lower.includes(marker));
}

/** True when connector refused exclusive because bit-perfect gate could not open HOG. */
export function isExclusiveHogUnavailableError(errMsg: string): boolean {
  return String(errMsg || '').toLowerCase().includes('exclusive_hog_unavailable');
}

export function isExclusiveDeviceBusyError(errMsg: string): boolean {
  return String(errMsg || '').startsWith('exclusive_device_busy:');
}

const DEVICE_BUSY_RETRY_MS = 750;
const SHARED_FALLBACK_RELEASE_MS = 500;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Shared fallback holds the WASAPI endpoint on the same local-native renderer.
 * Stop it before attempting exclusive so device_busy does not latch for minutes.
 */
export async function releaseSharedFallbackBeforeExclusive(): Promise<void> {
  const store = useMaestroStore.getState();
  if (store.usbOutputPath !== 'shared') return;

  const az = store.discovery?.activeZone as { type?: string } | null | undefined;
  const onSameLocalRenderer = az?.type === 'local-native' || !store.activeConnectorRenderer?.id;
  if (!onSameLocalRenderer) return;

  const playbackLive = store.rendererHasStream
    || store.playbackState === 'playing'
    || store.playbackState === 'loading';
  if (!playbackLive) return;

  console.warn('[exclusive] stopping shared fallback before exclusive attempt');
  store.setPlaybackState('paused');
  store.setRendererHasStream(false);

  const { TransportCommand } = await import('../transport/TransportCommand');
  await TransportCommand.pause(true).catch(() => {});
  await TransportCommand.stopExclusiveEngine().catch(() => {});
  await sleep(SHARED_FALLBACK_RELEASE_MS);
}

/** Retry exclusive open once after device_busy before shared fallback. */
export async function exclusivePlayWithDeviceBusyRetry(
  playFn: () => Promise<void>,
): Promise<void> {
  try {
    await playFn();
  } catch (err: unknown) {
    const msg = String((err as { message?: string })?.message || err || '');
    if (!isExclusiveDeviceBusyError(msg)) throw err;
    await sleep(DEVICE_BUSY_RETRY_MS);
    await playFn();
  }
}

/** True only when the fatal red banner should be shown (no shared fallback expected). */
export function shouldShowFatalExclusiveError(errMsg: string): boolean {
  return !isRecoverableExclusiveFailure(errMsg);
}

export function exclusiveFallbackReason(errMsg: string): ExclusiveFallbackReason {
  if (String(errMsg || '').startsWith('exclusive_device_busy:')) {
    return 'device_busy';
  }
  return 'format';
}

/**
 * Exclusive open failed — stop engine, log shared path, play shared, honest user notice.
 * Never sets exclusiveError (fatal banner).
 */
export async function runExclusiveSharedFallback(options: {
  errMsg: string;
  track: any;
  zone: any;
  branch: string;
  playShared: () => Promise<void>;
}): Promise<void> {
  const { errMsg, track, zone, branch, playShared } = options;
  const store = useMaestroStore.getState();

  console.warn(`[QC] exclusive open failed — falling back to shared (${branch})`, errMsg);
  const fallbackDevice = resolveZoneDisplayName(zone, {
    selectedUsbDac: store.selectedUsbDac,
    discoveredZones: store.discovery?.discoveredZones,
  }) || 'System Audio';
  const exclusiveDevice = resolveExclusiveDeviceName(zone, store.selectedUsbDac) || 'System Audio';
  logLocalOutputShared(exclusiveDevice, `exclusive_open_failed:${errMsg}`);

  const { TransportCommand } = await import('../transport/TransportCommand');
  await TransportCommand.stopExclusiveEngine().catch(() => {});

  diagnosticLog('info', 'playback', 'exclusive shared fallback', {
    branch,
    error: errMsg,
    recoverable: isRecoverableExclusiveFailure(errMsg),
  });

  store.setExclusiveError(null);
  store.setTransportPlaybackError(null);
  store.setUsbOutputPath('shared');

  try {
    await playShared();
    useMaestroStore.getState().setExclusiveError(null);

    const dac = useMaestroStore.getState().selectedUsbDac;
    const deviceKey = exclusiveFallbackDeviceNoticeKey(dac?.id || fallbackDevice);
    if (shouldShowExclusiveFallbackDeviceNotice(deviceKey)) {
      showImperativeToast(
        t('exclusive.fallback_toast', { device: fallbackDevice }),
        4500,
      );
    }
  } catch (fallbackErr: unknown) {
    const fallbackMsg = String((fallbackErr as { message?: string })?.message || fallbackErr || '');
    diagnosticLog('warn', 'playback', 'exclusive and shared fallback failed', {
      branch,
      exclusiveError: errMsg,
      fallbackError: fallbackMsg,
      device: fallbackDevice,
      recoverable: isRecoverableExclusiveFailure(errMsg),
    });

    const postStore = useMaestroStore.getState();
    postStore.setPlaybackState('paused');
    postStore.setRendererHasStream(false);
    postStore.setTransportPlaybackError(
      t('exclusive.playback_failed_transport', { device: fallbackDevice }),
    );
    throw fallbackErr;
  }
}

/** Clear stale fatal banner at the start of a user-initiated play. */
export function clearExclusiveErrorOnUserPlayStart(): void {
  const store = useMaestroStore.getState();
  store.setExclusiveError(null);
  store.setTransportPlaybackError(null);
}

/** Clear fatal banner once shared/exclusive playback is confirmed live. */
export function clearExclusiveErrorOnPlaybackConfirmed(): void {
  const store = useMaestroStore.getState();
  store.setExclusiveError(null);
  store.setTransportPlaybackError(null);
}
